import type {
  DateBound,
  DateFilter,
  DateUnit,
  MatchListValue,
  NumberPredicate,
} from '@/components/tree-dnd/tree-dnd-types';
import type { MatchListOption } from '@/components/tree-dnd/tree-dnd-conditions/mock-options';
import { detectDateFilterSelection } from '@/components/tree-dnd/tree-dnd-conditions/date-filter-utils';
import { DateTime } from 'luxon';

type Translate = {
  selectedCount: (arg: { count: number }) => string;
  matchAnyOf: (arg: { list: string }) => string;
  matchAllOf: (arg: { list: string }) => string;
  empty: string;
  anytime: string;
  onSendDay: string;
  dayBeforeSend: string;
  last7Days: string;
  last30Days: string;
  withinLast: (arg: { amount: number; unit: string }) => string;
  notLast7Days: string;
  notLast30Days: string;
  notWithinLast: (arg: { amount: number; unit: string }) => string;
  betweenDates: (arg: { from: string; to: string }) => string;
  afterDate: (arg: { date: string }) => string;
  beforeDate: (arg: { date: string }) => string;
  relativeBefore: (arg: { bound: string }) => string;
  relativeAfter: (arg: { bound: string }) => string;
  pickDate: string;
  days: string;
  weeks: string;
  months: string;
  years: string;
  beforeSend: string;
  boundEpoch: string;
};

function resolveLabels(ids: string[], options: MatchListOption[]) {
  const byId = new Map(options.map((option) => [option.id, option]));
  return ids
    .map((id) => byId.get(id)?.label)
    .filter((label): label is string => Boolean(label));
}

/** Join labels as "a" or "a e b" (conjunction is "e"/"ou"/"and"/"or"). */
export function formatNaturalList(
  labels: string[],
  conjunction: string
): string {
  if (labels.length === 0) return '';
  if (labels.length === 1) return labels[0]!;
  if (labels.length === 2) {
    return `${labels[0]} ${conjunction} ${labels[1]}`;
  }
  return `${labels.slice(0, -1).join(', ')} ${conjunction} ${labels[labels.length - 1]}`;
}

/**
 * Natural product phrase for readable filter sentences.
 * 1–2 items: "x" / "x e y" (all) or "x ou y" (any).
 * 3+ items: "todos os N produtos selecionados" / "ao menos um dos N produtos selecionados".
 */
export function formatNaturalMatchList(
  value: MatchListValue,
  options: MatchListOption[],
  t: {
    empty: string;
    and: string;
    or: string;
    manyAll: (arg: { count: number }) => string;
    manyAny: (arg: { count: number }) => string;
  }
): string {
  if (value.ids.length === 0) return t.empty;
  const labels = resolveLabels(value.ids, options);
  if (labels.length === 0) return t.empty;

  if (labels.length > 2) {
    const count = labels.length;
    return value.match === 'all' ? t.manyAll({ count }) : t.manyAny({ count });
  }

  return formatNaturalList(labels, value.match === 'all' ? t.and : t.or);
}

export function formatMatchListSummary(
  value: MatchListValue,
  options: MatchListOption[],
  t: Pick<
    Translate,
    'selectedCount' | 'matchAnyOf' | 'matchAllOf' | 'empty'
  > & { hideMatchMode?: boolean }
): string {
  if (value.ids.length === 0) {
    return t.empty;
  }

  const labels = resolveLabels(value.ids, options);
  const list =
    labels.length <= 2
      ? labels.join(', ')
      : t.selectedCount({ count: labels.length });

  if (t.hideMatchMode) {
    return list;
  }

  return value.match === 'all'
    ? t.matchAllOf({ list })
    : t.matchAnyOf({ list });
}

function formatIsoDate(date: string, pickDate: string): string {
  const parsed = DateTime.fromISO(date);
  return parsed.isValid ? parsed.toLocaleString(DateTime.DATE_MED) : pickDate;
}

function unitLabel(
  unit: DateUnit,
  t: Pick<Translate, 'days' | 'weeks' | 'months' | 'years'>
): string {
  const labels: Record<DateUnit, string> = {
    days: t.days,
    weeks: t.weeks,
    months: t.months,
    years: t.years,
  };
  return labels[unit];
}

function formatBound(
  bound: DateBound,
  t: Pick<
    Translate,
    | 'days'
    | 'weeks'
    | 'months'
    | 'years'
    | 'beforeSend'
    | 'boundEpoch'
    | 'pickDate'
  >
): string {
  if (bound.type === 'epoch') return t.boundEpoch;
  if (bound.type === 'absolute') return formatIsoDate(bound.date, t.pickDate);
  return `${bound.offset} ${unitLabel(bound.unit, t)} ${t.beforeSend}`;
}

export function formatNumberPredicateSummary(
  value: NumberPredicate,
  t: {
    exact: (arg: { value: number }) => string;
    atLeast: (arg: { value: number }) => string;
    atMost: (arg: { value: number }) => string;
  }
): string {
  switch (value.kind) {
    case 'exact':
      return t.exact({ value: value.value });
    case 'atLeast':
      return t.atLeast({ value: value.value });
    case 'atMost':
      return t.atMost({ value: value.value });
  }
}

export function formatDateFilterSummary(
  value: DateFilter,
  t: Omit<Translate, 'selectedCount' | 'matchAnyOf' | 'matchAllOf' | 'empty'>
): string {
  const selection = detectDateFilterSelection(value);

  switch (selection) {
    case 'anytime':
      return t.anytime;
    case 'onSendDay':
      return t.onSendDay;
    case 'dayBeforeSend':
      return t.dayBeforeSend;
    case 'last7Days':
      return t.last7Days;
    case 'last30Days':
      return t.last30Days;
    case 'withinLast':
      if (value.type === 'window' && value.from.type === 'relative') {
        return t.withinLast({
          amount: value.from.offset,
          unit: unitLabel(value.from.unit, t),
        });
      }
      break;
    case 'notLast7Days':
      return t.notLast7Days;
    case 'notLast30Days':
      return t.notLast30Days;
    case 'notWithinLast':
      if (value.type === 'window' && value.to.type === 'relative') {
        return t.notWithinLast({
          amount: value.to.offset,
          unit: unitLabel(value.to.unit, t),
        });
      }
      break;
    case 'customRelative':
      if (value.type === 'relative') {
        const bound = formatBound(value.date, t);
        return value.direction === 'before'
          ? t.relativeBefore({ bound })
          : t.relativeAfter({ bound });
      }
      break;
    case 'customAbsolute':
      if (value.type === 'absolute') {
        const date = formatIsoDate(value.date, t.pickDate);
        return value.direction === 'before'
          ? t.beforeDate({ date })
          : t.afterDate({ date });
      }
      break;
    case 'customWindow':
      if (value.type === 'window') {
        return t.betweenDates({
          from: formatBound(value.from, t),
          to: formatBound(value.to, t),
        });
      }
      break;
  }

  return t.anytime;
}

/** Lowercase the first letter so a standalone phrase can sit mid-sentence. */
export function uncapitalize(value: string): string {
  if (!value) return value;
  return value.charAt(0).toLocaleLowerCase() + value.slice(1);
}
